import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import { useAuthStore } from '@/stores/auth';

import { router } from './app/router';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import { NetworkToast } from './components/ui/NetworkToast';
import { ScreenshotToast } from './components/ui/ScreenshotToast';
import { initScreenGuard } from '@/lib/screenGuard';
import { initI18n } from './app/i18n';
import { initFreshness } from '@/lib/freshness';
import { initPush } from '@/lib/push';
import { loadAppConfig } from '@/lib/appConfig';
import { applyTextOverrides } from '@/app/i18n';
import { UpdateGate } from '@/components/UpdateGate';
import { lockViewport } from '@/lib/viewport';
import { initNativeShell, markReady } from '@/lib/nativeShell';
import { initNativeNav, installNativeTransitions } from '@/lib/nativeNav';
import { initLessonExit } from '@/lib/lessonExit';
import { initNetwork } from './lib/network';
import { initKeyboard } from './lib/keyboard';
import { initOutbox, registerOutboxHandler, setOutboxOwner } from './lib/outbox';
import { initLessonAudio, sendLessonAudio } from './lib/lessonAudioSend';
import type { LessonAudioPayload } from './lib/lessonAudio';
import { pruneDrafts } from './lib/recordingDraft';
import { initCrashReporter } from './lib/crashReporter';
import { markCleanExit } from './lib/crashReport';
import { progressApi } from './api/progress';
import { vocabApi } from './api/vocab';
import { trainersApi } from './api/trainers';
import { sprintApi } from './api/ieltsMock';

import './index.css';

// Initialise i18n before mounting React so the first render has the right strings.
initI18n();

// Отчёты о падениях (T23) — до первой отрисовки, чтобы поймать и ошибку первого кадра.
// На сайте ничего не делает: шлёт только приложение.
initCrashReporter();

// After a deploy, an already-open tab still references the previous build's
// hashed chunk names. Lazy-navigating then fails with "Failed to fetch
// dynamically imported module" because those files no longer exist on the
// server. Vite fires `vite:preloadError` in that case — reload once (guarded by
// sessionStorage so we never loop) to pull the fresh index.html + chunk names.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  // performance.now() avoids Date.now (not needed here) and dodges rapid loops.
  if (performance.now() - last > 10_000) {
    sessionStorage.setItem(KEY, String(performance.now()));
    markCleanExit();
    window.location.reload();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30s — most LMS data is moderately fresh (lessons, progress).
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      // Сохранённый ответ живёт неделю. Столько же имеет смысл показывать
      // урок, открытый в дороге: свежее подтянется, как только будет связь.
      gcTime: 7 * 24 * 60 * 60 * 1000,
    },
  },
});

/**
 * Офлайн-доступ к материалам.
 *
 * Кэш запросов сохраняется, поэтому приложение, открытое без сети, показывает
 * последние загруженные уроки, словарь и расписание вместо белого экрана.
 * Это НЕ скачивание материалов: файлы на устройство не выгружаются, видео с
 * YouTube офлайн не сохраняется, унести содержимое курса нельзя.
 *
 * Хранилище — localStorage, синхронное: восстановить кэш нужно ДО первого
 * рендера, иначе экран успеет моргнуть пустотой. Ответы учеников идут не сюда,
 * а в очередь отправки (`lib/outbox`), которая живёт в IndexedDB и потолком
 * localStorage не ограничена.
 */
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'onebilim:query-cache',
});

/**
 * 🔴 ПРИ ВЫХОДЕ КЭШ ВЫБРАСЫВАЕТСЯ ЦЕЛИКОМ.
 *
 * Без этого следующий вошедший в том же окне видел бы чужие данные. И это не
 * мелькание на долю секунды: сохранённый кэш живёт НЕДЕЛЮ и лежит в
 * `localStorage`, а ключи у многих запросов общие — `['curator','speaking-rooms']`,
 * `['admin','stories']`. Второй куратор на том же ноутбуке открыл бы список
 * первого и работал бы с ним, пока не подтянется свежий ответ.
 *
 * Тем же приёмом уже закрыты уведомления при выходе (`disablePush`): общий
 * компьютер в школе — обычное дело, а не редкость.
 *
 * Найдено своим аудитом 05.09.2026.
 */
useAuthStore.subscribe((state, prev) => {
  // ⚠️ Очередь обязана ЗНАТЬ, чей сейчас сеанс. Ответы одного ученика не должны
  // уходить пропуском другого: устройство часто общее — школьный компьютер,
  // телефон на двоих. Сообщаем и при входе, и при выходе.
  setOutboxOwner(state.user?.id);

  if (prev.accessToken && !state.accessToken) {
    queryClient.clear();
    try {
      window.localStorage.removeItem('onebilim:query-cache');
    } catch {
      /* приватное окно или запрет на хранение — чистить нечего */
    }
  }
});

// Состояние сети и очередь неотправленного поднимаем до первого рендера: очередь
// должна попытаться разгрузиться сразу при запуске, а не после открытия экрана.
setOutboxOwner(useAuthStore.getState().user?.id);
initNetwork();
initKeyboard();
// Запрет масштабирования — только в приложении, в браузере зум остаётся.
lockViewport();
// Приложение из магазина: класс `native` на <html> и заставка до готовности. В браузере — ничего.
initNativeShell();
// Свайп назад от края на iPhone: где он включён, решает страница (`lib/nativeNav.ts`). В браузере — ничего.
initNativeNav(router);
// Откуда вошли в урок — чтобы «Назад» и «Завершить урок» выводили туда же (`lib/lessonExit.ts`). Сайт и приложение.
initLessonExit(router);
// Предупреждение о снимке экрана — только iOS-приложение (D62, `lib/screenGuard.ts`). В браузере — ничего.
initScreenGuard();
// Переходы между экранами как в iOS — только в приложении (`lib/nativeNav.ts`).
installNativeTransitions(router);
// Нажатие на пуш и пуш при открытом приложении (`lib/push.ts`, сборка 1.1.1). ⚠️ Подписка — ДО отрисовки
// роутера: при холодном старте плагин держит нажатие до первого подписчика. В браузере — ничего.
initPush(router, queryClient, {
  ready: () =>
    new Promise<void>((resolve) => {
      if (useAuthStore.persist.hasHydrated()) resolve();
      else useAuthStore.persist.onFinishHydration(() => resolve());
    }),
  user: () => useAuthStore.getState().user,
});
// Обновление данных при возвращении к приложению: без него выданный админом
// доступ не появлялся у ученика на экране (найдено 21.08.2026).
initFreshness(queryClient);
registerOutboxHandler<{ blockId: string; answer_data: string; is_correct: boolean; block_type?: string }>(
  'lesson-answer',
  async ({ blockId, ...payload }) => {
    await progressApi.answerBlock(blockId, payload);
    // ⚠️ СБРАСЫВАЕМ СПИСОК ДОМАШКИ. Сочинение и запись голоса уходят куратору
    // на проверку, и в разделе «Домашка» должна появиться работа. Без сброса
    // ученик открывает вкладку «На проверке» и видит СТАРЫЙ ответ — пусто,
    // хотя работа уже сдана. Заказчик так это и поймал 06.09.2026: во «Всех»
    // работа есть, во вкладке «На проверке» её нет.
    //
    // Сбрасываем на любой ответ, а не только на открытый: тип задания знает
    // сервер, а не очередь, и гадать здесь — значит однажды не угадать.
    queryClient.invalidateQueries({ queryKey: ['student', 'homework'] });
  },
);
// Раздел урока пройден — без очереди отметка терялась при обрыве связи, и урок
// в марафоне оставался незакрытым, хотя ученик его прошёл.
registerOutboxHandler<{ sectionId: string }>('section-complete', ({ sectionId }) =>
  progressApi.completeSection(sectionId),
);
// Повторение слова: сюда уходит вся память тренажёра о том, что ученик выучил.
registerOutboxHandler<{
  entry_id: string;
  quality: 0 | 1 | 2 | 3 | 4 | 5;
  sense_index?: number;
  // ⚠️ Способ обязан доехать: у каждого свой счёт, и без него ответ зачтётся в
  // карточку, каким бы упражнением человек ни отвечал на самом деле.
  mode?: 'card' | 'choose' | 'sentence' | 'audio' | 'type';
  // ⚠️ Набранный текст — для способов «набрать по буквам» и «на слух». По нему
  // вердикт выносит СЕРВЕР, а наш `quality` отбрасывается. В очереди лежит
  // именно текст, а не готовое «верно/неверно»: ответ может пролежать до
  // возвращения связи, и решать по нему должен тот, у кого эталон.
  typed?: string;
}>('vocab-review', (payload) => vocabApi.review(payload));
// Черновик спринта: ученик мог отвечать полчаса без связи — терять это нельзя.
registerOutboxHandler<{ attemptId: string; answers: Record<string, unknown> }>(
  'sprint-progress',
  ({ attemptId, answers }) => sprintApi.saveProgress(attemptId, answers),
);
// Голосовой ответ в уроке. Единственное действие, где в очереди лежит не текст,
// а сам файл записи: ссылки на неё до загрузки просто не существует. Поэтому
// обработчик делает два шага подряд — грузит запись в хранилище и только потом
// отмечает ответ настоящей ссылкой.
//
// До этого запись уходила отдельным запросом мимо очереди: оборвалась связь —
// и работа ученика пропадала совсем (задача 120). Хранилище браузера умеет
// хранить файлы целиком, так что запись переживает и закрытие приложения.
//
// ⚠️ Если загрузка прошла, а отметка ответа нет, повтор загрузит файл заново —
// в хранилище останется лишняя копия. Это дешевле, чем потерять запись.
//
// Сама отправка — `lib/lessonAudioSend.ts` (вынесена 02.10.2026, чтобы её можно было проверять без запуска
// приложения): байты из очереди или из файла на телефоне, загрузка, отметка ответа, фазы для блока задания.
registerOutboxHandler<LessonAudioPayload>('lesson-audio', async (payload) => {
  await sendLessonAudio(payload);
  // ⚠️ Тот же сброс, что и у текстового ответа: запись голоса тоже уходит
  // куратору, и в разделе «Домашка» должна появиться работа.
  queryClient.invalidateQueries({ queryKey: ['student', 'homework'] });
});
// Файл записи на телефоне и заметка ученику об отказе — когда запись уходит из очереди.
initLessonAudio();
// Ответ в адаптивном тренажёре — он же начисляет очки CEFR.
registerOutboxHandler<{ itemId: string; answerData: string }>('drill-answer', ({ itemId, answerData }) =>
  trainersApi.drillAnswer(itemId, answerData),
);
initOutbox();
// Просроченные черновики записи убираем на старте: иначе они копились бы в
// хранилище, где живёт и очередь неотправленных ответов.
void pruneDrafts();

// Настройки приложения с нашего сервера: какие возможности показывать, какие
// числа брать, не пора ли обновиться.
//
// ⚠️ НЕ ЖДЁМ ОТВЕТА НАМЕРЕННО. Экран рисуется сразу на умолчаниях, зашитых в
// сборку. Если ждать сеть, ученик в метро смотрел бы на пустой экран из-за
// служебного запроса, без которого приложение прекрасно работает.
// Настройки приходят — и вместе с ними правки текстов: их накладываем сразу,
// чтобы поправленная строка дошла до приложения без новой сборки.
void loadAppConfig().then(applyTextOverrides);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        dehydrateOptions: {
          // В сохранённый кэш идут только удавшиеся запросы: незачем
          // восстанавливать ошибку сети как «данные».
          shouldDehydrateQuery: (q) => q.state.status === 'success',
        },
      }}
      // Заставка приложения уходит, когда сохранённый кэш уже в памяти: первый экран — последний виденный,
      // а не пустота. Ошибка чтения кэша тоже конец ожидания — показываем то, что есть.
      onSuccess={() => markReady('cache')}
      onError={() => markReady('cache')}
    >
      <ConfirmProvider>
        {/* Последняя страховка совместимости: если сервер прямо сказал, что эта
            версия больше не работает, честно просим обновиться вместо
            бесконечных ошибок. По умолчанию пускает. */}
        <UpdateGate>
          <RouterProvider router={router} />
        </UpdateGate>
        {/* «Нет соединения» — одна всплывашка на всё приложение, вне кабинета: вход тоже (lib/networkNotice.ts). */}
        <NetworkToast />
        {/* После снимка экрана в iOS-приложении — предупреждение (D62, lib/screenGuard.ts). */}
        <ScreenshotToast />
      </ConfirmProvider>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
